import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Plus, X } from 'lucide-react';
import { useFieldArray } from 'react-hook-form';
import { limitMessage, limitRemaining } from '@relay-towing/api';
import {
  Alert,
  Button,
  Form,
  FormField,
  Input,
  Select,
  applyServerErrors,
  useZodForm,
  startDateToIso,
} from '@relay-towing/ui';
import { useCreateDriver } from '@/api/hooks/useCreateDriver';
import { useGetTrucks } from '@/api/hooks/useGetTrucks';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import { usePlan, usePlanUsage } from '@/hooks/usePlan';
import { links } from '@/lib/links';
import { hasV1Bridge, openExternal } from '@/lib/nativeBridge';
import { useToast } from '@/contexts/ToastContext';
import OnboardingStepChrome from '@/screens/onboarding/OnboardingStepChrome';
import { STEPS } from '@/screens/onboarding/onboardingSteps';
import {
  createDriverSchema,
  createDriversSchema,
  emptyDriverRow,
  type CreateDriversValues,
  type DriverRow,
} from './createDriverSchema';

/**
 * The plan's driver limit: how many more drivers fit, and once none do, an
 * alert naming the plan that allows more. Whoever can buy a plan gets
 * Upgrade, which opens the dashboard's billing outside the app; everyone else is told to
 * ask their owner. The backend refuses a driver past the limit either way,
 * once the plan is enforced; until then nothing stops one.
 *
 * A phone build from before bridge v2 cannot open billing outside the app:
 * openExternal goes there in place, and the old iPhone build has no way
 * back from the dashboard. So there the owner is told where to upgrade.
 */
function useDriverLimit() {
  const { catalogue, canUpgrade } = usePlan();
  const { data: usage } = usePlanUsage();
  const remaining = usage ? limitRemaining(usage, 'drivers') : Infinity;
  const upgradeHere = canUpgrade && !hasV1Bridge();
  const alert =
    usage && catalogue && remaining <= 0 ? (
      <Alert
        tone="destructive"
        actions={
          upgradeHere ? (
            <Button size="sm" onClick={() => openExternal(links.billing)}>
              Upgrade
            </Button>
          ) : undefined
        }
      >
        {limitMessage(catalogue, usage.plan.id, 'drivers', usage.usage.drivers)}
        {upgradeHere
          ? null
          : canUpgrade
            ? ' Upgrade from the dashboard, in Settings → Billing.'
            : ' Ask your owner to upgrade.'}
      </Alert>
    ) : null;
  return { remaining, alert };
}

interface CreateDriverScreenProps {
  onBack: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
  showHeader?: boolean;
  /** When false, truck assignment UI is hidden and no trucks list is fetched (e.g. onboarding). */
  showTruckSection?: boolean;
  variant?: 'default' | 'onboarding';
}

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On Leave' },
];

// ─── Default (single-driver) form ────────────────────────────────────────────

interface DefaultFormProps {
  onBack: () => void;
  onSuccess?: () => void;
  showTruckSection: boolean;
}

const DefaultForm: React.FC<DefaultFormProps> = ({
  onBack,
  onSuccess,
  showTruckSection,
}) => {
  const { showToast } = useToast();
  const { driver } = useDriverStateV2();
  const { mutate: createDriver, isPending: isCreatingDriver } =
    useCreateDriver();
  const { data: trucksData, isLoading: isTrucksLoading } = useGetTrucks(
    1,
    100,
    '',
    showTruckSection
  );
  const driverLimit = useDriverLimit();
  const isAtDriverLimit = driverLimit.remaining <= 0;

  const form = useZodForm(createDriverSchema, {
    defaultValues: emptyDriverRow(),
  });

  const truckOptions = isTrucksLoading
    ? [{ value: 'loading', label: 'Loading trucks...', disabled: true }]
    : (trucksData?.data ?? []).length
      ? (trucksData?.data ?? []).map((truck) => ({
          value: truck.id,
          label: `${truck.truckNumber || truck.licensePlate || truck.id}${
            truck.towRating ? ` - ${truck.towRating.toLocaleString()}lbs` : ''
          }`,
        }))
      : [{ value: 'none', label: 'No trucks available', disabled: true }];

  const onSubmit = (data: DriverRow) => {
    if (!driver?.companyId) {
      showToast('error', 'No company ID available. Please try again.');
      return;
    }

    createDriver(
      {
        ...data,
        assignedTruckId: data.assignedTruckId || undefined,
        startDate: startDateToIso(data.startDate),
      },
      {
        onSuccess: () => {
          showToast(
            'success',
            `Driver ${data.name} has been added successfully!`
          );
          form.reset(emptyDriverRow());
          if (onSuccess) {
            onSuccess();
          } else {
            onBack();
          }
        },
        // A field the backend names gets its message; anything else shows at
        // the top of the form, where it stays while they fix it.
        onError: (error) =>
          applyServerErrors(
            form,
            error,
            `Failed to add driver: ${error?.message || 'Unknown error'}`
          ),
      }
    );
  };

  return (
    <Form form={form} gap={4} onSubmit={onSubmit}>
      <FormField form={form} name="name" label="Name">
        {(field) => <Input {...field} placeholder="Enter full name" />}
      </FormField>

      <FormField form={form} name="email" label="Email">
        {(field) => (
          <Input type="email" {...field} placeholder="Enter email address" />
        )}
      </FormField>

      <FormField form={form} name="phone" label="Phone">
        {(field) => (
          <Input type="tel" {...field} placeholder="(xxx) xxx-xxxx" />
        )}
      </FormField>

      {showTruckSection && (
        <FormField form={form} name="assignedTruckId" label="Truck">
          {(field) => (
            <Select
              options={truckOptions}
              placeholder="Select a truck"
              value={field.value || undefined}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
      )}

      <FormField form={form} name="status" label="Status">
        {(field) => (
          <Select
            options={STATUS_OPTIONS}
            value={field.value}
            onValueChange={field.onChange}
          />
        )}
      </FormField>

      <FormField form={form} name="startDate" label="Start Date">
        {(field) => <Input type="date" {...field} />}
      </FormField>

      <div
        style={{ backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }}
        className="border rounded-lg p-3"
      >
        <p style={{ color: '#1E40AF' }} className="text-xs">
          <strong>Note:</strong> An invitation email will be automatically sent
          to the driver's email address.
        </p>
      </div>

      {driverLimit.alert}

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#D1D5DB',
            color: '#374151',
          }}
          className="flex-1 py-3 px-4 border rounded-lg font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreatingDriver || isAtDriverLimit}
          style={{
            backgroundColor:
              isCreatingDriver || isAtDriverLimit ? '#1F2937' : '#000000',
            color: '#FFFFFF',
          }}
          className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isCreatingDriver && <Loader2 size={20} className="animate-spin" />}
          {isCreatingDriver ? 'Adding...' : 'Add Driver'}
        </button>
      </div>
    </Form>
  );
};

// ─── Onboarding (multi-driver) form ──────────────────────────────────────────

interface OnboardingFormProps {
  onSuccess?: () => void;
  onSkip?: () => void;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({
  onSuccess,
  onSkip,
}) => {
  const { showToast } = useToast();
  const { driver } = useDriverStateV2();
  const { mutateAsync: createDriver } = useCreateDriver();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const driverLimit = useDriverLimit();
  const remainingSlots = driverLimit.remaining;

  const form = useZodForm(createDriversSchema, {
    defaultValues: { drivers: [emptyDriverRow()] },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'drivers',
  });

  const canAddMore = fields.length < remainingSlots;

  const onSubmit = async (data: CreateDriversValues) => {
    if (!driver?.companyId) {
      showToast('error', 'No company ID available. Please try again.');
      return;
    }

    const rows = data.drivers;

    /*
     * Every row is complete by the time we get here — the schema sees to that,
     * where this used to be three toasts for three ways of being unfinished.
     * The plan limit is the one rule the schema cannot know.
     */
    if (remainingSlots !== Infinity && rows.length > remainingSlots) {
      form.setError('root.server', {
        message: `You can only add ${remainingSlots} more driver(s) on your current plan.`,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      for (const row of rows) {
        await createDriver({
          name: row.name.trim(),
          email: row.email.trim(),
          phone: row.phone?.trim() || undefined,
          status: row.status,
          startDate: startDateToIso(row.startDate),
          assignedTruckId: row.assignedTruckId || undefined,
        });
      }

      showToast(
        'success',
        rows.length === 1
          ? `Driver ${rows[0].name} has been added successfully!`
          : `${rows.length} drivers have been added successfully!`
      );
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      applyServerErrors(form, err, `Failed to add driver: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      gap={0}
      onSubmit={onSubmit}
      className="flex flex-col flex-1"
    >
      <div className="flex-1 px-4 py-5 space-y-4 overflow-y-auto">
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Add drivers to receive dispatch notifications via the Mobile app.
        </p>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="rounded-xl border p-4 space-y-3"
            style={{ borderColor: '#E5E7EB' }}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: '#111827' }}>
                Driver {index + 1}
              </p>
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="p-1 rounded-lg"
                  style={{ color: '#6B7280' }}
                  aria-label={`Remove Driver ${index + 1}`}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <FormField
              form={form}
              name={`drivers.${index}.name`}
              label="Name"
              size="sm"
            >
              {(bound) => (
                <Input {...bound} size="sm" placeholder="Full name" />
              )}
            </FormField>

            <FormField
              form={form}
              name={`drivers.${index}.email`}
              label="Email"
              size="sm"
            >
              {(bound) => (
                <Input
                  {...bound}
                  size="sm"
                  type="email"
                  placeholder="Email address"
                />
              )}
            </FormField>

            <FormField
              form={form}
              name={`drivers.${index}.phone`}
              label="Phone"
              size="sm"
            >
              {(bound) => (
                <Input
                  {...bound}
                  size="sm"
                  type="tel"
                  placeholder="(xxx) xxx-xxxx"
                />
              )}
            </FormField>

            <FormField
              form={form}
              name={`drivers.${index}.status`}
              label="Status"
              size="sm"
            >
              {(bound) => (
                <Select
                  options={STATUS_OPTIONS}
                  size="sm"
                  value={bound.value}
                  onValueChange={bound.onChange}
                />
              )}
            </FormField>

            <FormField
              form={form}
              name={`drivers.${index}.startDate`}
              label="Start Date"
              size="sm"
            >
              {(bound) => <Input {...bound} size="sm" type="date" />}
            </FormField>
          </div>
        ))}

        {/* Add another driver */}
        {canAddMore && (
          <button
            type="button"
            onClick={() => append(emptyDriverRow())}
            className="w-full py-3 rounded-xl border-2 border-dashed text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            style={{ borderColor: '#D1D5DB', color: '#6B7280' }}
          >
            <Plus size={16} />
            Add another driver
          </button>
        )}

        {/* Note banner */}
        <div
          style={{ backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }}
          className="border rounded-lg p-3"
        >
          <p style={{ color: '#1E40AF' }} className="text-xs">
            <strong>Note:</strong> An invitation email will be automatically
            sent to the driver's email address.
          </p>
        </div>

        {driverLimit.alert}
      </div>

      {/* Footer */}
      <div
        className="border-t px-4 pb-10 pt-4 space-y-3"
        style={{ borderColor: '#E5E7EB' }}
      >
        <button
          type="submit"
          disabled={
            isSubmitting || (remainingSlots !== Infinity && remainingSlots <= 0)
          }
          className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity"
          style={{
            backgroundColor: '#172F27',
            opacity:
              isSubmitting ||
              (remainingSlots !== Infinity && remainingSlots <= 0)
                ? 0.5
                : 1,
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Adding drivers…
            </>
          ) : (
            <>
              Continue
              <ArrowRight size={18} />
            </>
          )}
        </button>

        {onSkip && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm font-medium"
              style={{ color: '#6B7280' }}
            >
              Skip for now
            </button>
          </div>
        )}
      </div>
    </Form>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const CreateDriverScreen: React.FC<CreateDriverScreenProps> = ({
  onBack,
  onSuccess,
  onSkip,
  showTruckSection = true,
  variant = 'default',
}) => {
  const { driver } = useDriverStateV2();

  if (!driver?.companyId) {
    return (
      <div
        style={{ backgroundColor: '#FFFFFF' }}
        className="min-h-screen flex flex-col items-center justify-center px-4"
      >
        <h2 style={{ color: '#DC2626' }} className="text-lg font-semibold mb-2">
          No Company Access
        </h2>
        <p style={{ color: '#4B5563' }} className="text-sm text-center mb-6">
          You don't have access to any company. Please contact your
          administrator.
        </p>
        <button
          onClick={onBack}
          style={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }}
          className="py-2 px-6 rounded-lg font-medium"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (variant === 'onboarding') {
    return (
      <div
        style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}
        className="flex flex-col"
      >
        <OnboardingStepChrome
          filledSegmentCount={2}
          segmentCount={STEPS.length}
          title="Add Drivers"
          subtitle="Invite your team"
          onBack={onBack}
        />
        <OnboardingForm onSuccess={onSuccess} onSkip={onSkip} />
      </div>
    );
  }

  return (
    <div
      style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}
      className="flex flex-col pb-20"
    >
      <div
        style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
        className="flex items-center gap-3 p-4 border-b"
      >
        <button
          onClick={onBack}
          className="p-2 rounded-lg transition-colors"
          style={{ backgroundColor: 'transparent' }}
        >
          <ArrowLeft size={24} style={{ color: '#4B5563' }} />
        </button>
        <h1 style={{ color: '#000000' }} className="text-xl font-semibold">
          Add New Driver
        </h1>
      </div>

      <div className="flex-1 px-4 py-6 overflow-y-auto">
        <DefaultForm
          onBack={onBack}
          onSuccess={onSuccess}
          showTruckSection={showTruckSection}
        />
      </div>
    </div>
  );
};

export default CreateDriverScreen;
